import { useEffect, useRef, useImperativeHandle, forwardRef, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { feature } from "topojson-client";
import { geoCentroid } from "d3-geo";
import topo from "../../assets/world-110m.json";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

// Antimeridian-hulpfuncties: landen die 180° kruisen (Fiji, Rusland) trekken anders
// een horizontale streep over de kaart.
const _crosses = (coords) => {
  let pos = false, neg = false;
  const walk = (c) => {
    if (typeof c[0] === "number") { if (c[0] > 150) pos = true; if (c[0] < -150) neg = true; }
    else c.forEach(walk);
  };
  walk(coords);
  return pos && neg;
};
const _shift = (c) => (typeof c[0] === "number" ? [c[0] < 0 ? c[0] + 360 : c[0], c[1]] : c.map(_shift));

// Genormaliseerde FeatureCollection: stabiele string-id per land (matcht dataset-ids).
const FC = (() => {
  const fc = feature(topo, topo.objects.countries);
  fc.features = fc.features
    .filter((f) => f.id != null && String(f.id) !== "010") // Antarctica weglaten
    .map((f) => {
      let geometry = f.geometry;
      if (_crosses(geometry.coordinates)) {
        geometry = { ...geometry, coordinates: _shift(geometry.coordinates) };
      }
      return { ...f, id: String(f.id), geometry, properties: { ...f.properties, iso: String(f.id) } };
    });
  return fc;
})();

const cv = (el, name) => (el ? getComputedStyle(el).getPropertyValue(name).trim() : "") || "#8a8f98";

const parseColor = (s) => {
  if (!s) return [136, 143, 152];
  s = s.trim();
  if (s[0] === "#") {
    let h = s.slice(1);
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  const m = s.match(/(\d+\.?\d*)/g);
  return m ? [+m[0], +m[1], +m[2]] : [136, 143, 152];
};
const blend = (c1, c2, t) => {
  const a = parseColor(c1), b = parseColor(c2);
  const v = a.map((x, i) => Math.round(x + (b[i] - x) * t));
  return "#" + v.map((x) => Math.max(0, Math.min(255, x)).toString(16).padStart(2, "0")).join("");
};

export const MapboxWorldMap = forwardRef(function MapboxWorldMap({ idToCode, onReady }, ref) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markers = useRef({});
  const lastHl = useRef(null);
  const playable = useRef([]);
  const touched = useRef(new Set());

  const codeToId = useMemo(() => {
    const m = {};
    Object.entries(idToCode).forEach(([id, code]) => { m[code] = id; });
    return m;
  }, [idToCode]);

  const featByCodes = (codes) => FC.features.filter((f) => codes.includes(idToCode[f.id]));

  // Veilige setFeatureState-wrapper (Mapbox kan tijdens load gooien)
  const ss = (id, state) => {
    const map = mapRef.current;
    if (!map || !id) return;
    try { map.setFeatureState({ source: "countries", id }, state); touched.current.add(id); } catch (e) {}
  };

  useImperativeHandle(ref, () => ({
    hitTest(clientX, clientY) {
      const map = mapRef.current; if (!map) return null;
      const r = map.getContainer().getBoundingClientRect();
      let feats = [];
      try { feats = map.queryRenderedFeatures([clientX - r.left, clientY - r.top], { layers: ["countries-fill"] }); } catch (e) {}
      if (!feats.length) return null;
      return idToCode[String(feats[0].id)] || null;
    },
    setHighlight(code) {
      const id = code ? codeToId[code] : null;
      if (lastHl.current && lastHl.current !== id) ss(lastHl.current, { highlight: false });
      if (id) ss(id, { highlight: true });
      lastHl.current = id;
    },
    markCorrect(code) {
      const map = mapRef.current; if (!map) return;
      const id = codeToId[code]; if (!id) return;
      ss(id, { correct: true, highlight: false });
      lastHl.current = null;
      const f = FC.features.find((x) => x.id === id);
      if (f && !markers.current[code]) {
        const c = geoCentroid(f);
        if (!c || isNaN(c[0])) return;
        const el = document.createElement("div");
        el.style.cssText = "width:30px;height:21px;border-radius:4px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4);background-size:cover;background-position:center;transform:scale(0);transition:transform .35s cubic-bezier(.34,1.56,.64,1);background-image:url(https://flagcdn.com/w40/" + code + ".png)";
        try { const mk = new mapboxgl.Marker({ element: el }).setLngLat([c[0], c[1]]).addTo(map); requestAnimationFrame(() => { el.style.transform = "scale(1)"; }); markers.current[code] = mk; } catch (e) {}
      }
    },
    flashWrong(code) {
      if (!code) return;
      const id = codeToId[code]; if (!id) return;
      ss(id, { wrong: true });
      setTimeout(() => ss(id, { wrong: false }), 650);
    },
    setPlayable(codes) {
      playable.current.forEach((id) => ss(id, { playable: false }));
      const ids = codes.map((c) => codeToId[c]).filter(Boolean);
      ids.forEach((id) => ss(id, { playable: true }));
      playable.current = ids;
    },
    fitToCodes(codes) {
      const map = mapRef.current; if (!map) return;
      try { map.resize(); } catch (e) {}
      const fs = featByCodes(codes);
      if (!fs.length) return;
      // Centroïdes gebruiken i.p.v. ruwe bounds (vermijdt overzeese gebieden zoals Frans-Guyana/Spitsbergen)
      let minLng = 180, minLat = 90, maxLng = -180, maxLat = -90;
      fs.forEach((f) => {
        const c = geoCentroid(f);
        if (!c || isNaN(c[0])) return;
        minLng = Math.min(minLng, c[0]); maxLng = Math.max(maxLng, c[0]);
        minLat = Math.min(minLat, c[1]); maxLat = Math.max(maxLat, c[1]);
      });
      if (minLng > maxLng) return;
      const padX = Math.max(6, (maxLng - minLng) * 0.18);
      const padY = Math.max(5, (maxLat - minLat) * 0.18);
      try {
        map.fitBounds([[minLng - padX, minLat - padY], [maxLng + padX, maxLat + padY]],
          { padding: { top: 120, bottom: 170, left: 30, right: 30 }, duration: 700, maxZoom: 6 });
      } catch (e) {}
    },
    clearAll() {
      const map = mapRef.current; if (!map) return;
      Object.values(markers.current).forEach((m) => { try { m.remove(); } catch (e) {} });
      markers.current = {};
      touched.current.forEach((id) => ss(id, { playable: false, correct: false, highlight: false, wrong: false }));
      touched.current.clear();
      playable.current = [];
      lastHl.current = null;
    },
  }));

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const C = (n) => cv(el, n);
    const map = new mapboxgl.Map({
      container: el,
      style: { version: 8, sources: {}, glyphs: "mapbox://fonts/mapbox/{fontstack}/{range}.pbf", layers: [{ id: "bg", type: "background", paint: { "background-color": C("--t-surface") } }] },
      center: [10, 30], zoom: 1.2, dragRotate: false, pitchWithRotate: false, attributionControl: false, renderWorldCopies: false,
    });
    mapRef.current = map;

    map.on("load", () => {
      map.addSource("countries", { type: "geojson", data: FC, promoteId: "iso", buffer: 128 });
      const contextCol = blend(C("--t-surface"), C("--t-muted"), 0.24);
      map.addLayer({
        id: "countries-fill", type: "fill", source: "countries",
        paint: {
          "fill-color": ["case",
            ["boolean", ["feature-state", "correct"], false], C("--t-success"),
            ["boolean", ["feature-state", "wrong"], false], "#ef4444",
            ["boolean", ["feature-state", "highlight"], false], C("--t-accent"),
            ["boolean", ["feature-state", "playable"], false], C("--t-surface2"),
            contextCol],
          "fill-opacity": 1,
        },
      });
      map.addLayer({
        id: "countries-line", type: "line", source: "countries",
        paint: {
          "line-color": ["case", ["boolean", ["feature-state", "playable"], false], C("--t-primary"), C("--t-border")],
          "line-width": ["case", ["boolean", ["feature-state", "playable"], false], 1.6, 0.5],
        },
      });
      // onReady pas vuren als de GeoJSON-bron klaar is (setFeatureState faalt anders)
      const onSrc = (e) => {
        if (e.sourceId === "countries" && map.isSourceLoaded("countries")) {
          map.off("sourcedata", onSrc);
          onReady && onReady();
        }
      };
      map.on("sourcedata", onSrc);
    });

    if (typeof window !== "undefined") window.__wmap = map;

    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line
  }, []);

  return <div ref={containerRef} data-testid="mapbox-container" className="absolute inset-0 w-full h-full" />;
});
